import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "components/AppIcon";

// ── Mode config: icon + colour per payment mode ───────────────────────────────
const MODE_CONFIG = {
  "Bank Deposit": {
    icon: "Building2",
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  },
  Cash: {
    icon: "Banknote",
    color:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  },
  Cheque: {
    icon: "FileCheck",
    color:
      "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  },
  "Credit Card": {
    icon: "CreditCard",
    color:
      "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
  },
  "Debit Card": {
    icon: "CreditCard",
    color:
      "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
  },
  "Demand Draft": {
    icon: "FileText",
    color:
      "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  },
  "Online Payment Gateway": {
    icon: "Globe",
    color: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
  },
  "Online Transfer": {
    icon: "ArrowRightLeft",
    color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
  },
  PayPal: {
    icon: "Wallet",
    color: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  },
  "Wire Transfer": {
    icon: "Zap",
    color: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300",
  },
};

const PaymentTable = ({ payments, onEdit, onDelete, onVoid }) => {
  if (payments.length === 0) return null;

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-visible">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Client
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Payment Date
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Mode
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Amount
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Reference
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Comments
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {payments.map((payment, index) => (
              <PaymentRow
                key={payment.id}
                payment={payment}
                index={index}
                onEdit={onEdit}
                onDelete={onDelete}
                onVoid={onVoid}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PaymentRow = ({ payment, index, onEdit, onDelete, onVoid }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);

  const isVoided = payment.status === "VOID";
  const isAccounted = !!payment.journalEntryId;

  const modeConfig = MODE_CONFIG[payment.paymentMode] || {
    icon: "CircleDollarSign",
    color: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  };

  const actions = [
    ...(!isAccounted && !isVoided
      ? [
          {
            icon: "Edit3",
            label: "Edit Payment",
            color: "text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20",
            onClick: () => onEdit(payment),
          },
        ]
      : []),

    {
      icon: "Receipt",
      label: "View Receipt",
      color: "text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20",
      onClick: () => {},
    },

    ...(isAccounted && !isVoided
      ? [
          {
            icon: "RotateCcw",
            label: "Void Payment",
            color:
              "text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20",
            onClick: () => setShowVoidConfirm(true),
          },
        ]
      : []),

    ...(!isAccounted && !isVoided
      ? [
          {
            icon: "Trash2",
            label: "Delete Payment",
            color: "text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20",
            onClick: () => setShowDeleteConfirm(true),
          },
        ]
      : []),
  ];

  // Avatar initials from client name
  const initials = (payment.clientName || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const avatarColors = [
    "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
    "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
    "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
    "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
  ];
  const avatarColor = avatarColors[index % avatarColors.length];

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.04 }}
        className="hover:bg-muted/50 transition-colors"
      >
        {/* Client */}
        <td className="px-6 py-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${avatarColor}`}
            >
              {initials}
            </div>
            <div className="font-medium text-foreground">
              {payment.clientName || "—"}
            </div>
          </div>
        </td>

        {/* Payment Date */}
        <td className="px-6 py-4">
          <div className="text-sm text-foreground">
            {payment.paymentDate
              ? new Date(payment.paymentDate).toLocaleDateString("en-SA", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              : "—"}
          </div>
        </td>

        {/* Mode Badge */}
        <td className="px-6 py-4">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${modeConfig.color}`}
          >
            <Icon name={modeConfig.icon} size={12} />
            {payment.paymentMode}
          </span>
        </td>

        {/* Amount */}
        <td className="px-6 py-4">
          <div className="font-semibold text-foreground">
            SR{" "}
            {parseFloat(payment.amount || 0).toLocaleString("en-SA", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
          {isVoided && (
            <div className="text-xs text-red-500 font-medium mt-1">VOID</div>
          )}
          {isAccounted && !isVoided && (
            <div className="text-xs text-green-600 font-medium mt-1">
              Accounted
            </div>
          )}
        </td>

        {/* Reference */}
        <td className="px-6 py-4">
          {payment.reference ? (
            <span className="text-sm font-mono text-foreground bg-muted px-2 py-0.5 rounded">
              {payment.reference}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </td>

        {/* Comments */}
        <td className="px-6 py-4">
          <p
            className="text-sm text-muted-foreground max-w-[160px] truncate"
            title={payment.comments}
          >
            {payment.comments || "—"}
          </p>
        </td>

        {/* Actions */}
        <td className="px-6 py-4">
          <div className="flex items-center gap-1">
            {actions.map((action) => (
              <ActionIconButton
                key={action.label}
                icon={action.icon}
                label={action.label}
                colorClass={action.color}
                onClick={action.onClick}
              />
            ))}
          </div>
        </td>
      </motion.tr>

      {/* Inline delete confirmation */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.tr
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <td
              colSpan={7}
              className="px-6 py-3 bg-red-50 dark:bg-red-900/10 border-b border-red-200 dark:border-red-800"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-700 dark:text-red-300 font-medium">
                  Delete this payment of{" "}
                  <strong>
                    SR{" "}
                    {parseFloat(payment.amount || 0).toLocaleString("en-SA", {
                      minimumFractionDigits: 2,
                    })}
                  </strong>{" "}
                  from <strong>{payment.clientName}</strong>? This cannot be
                  undone.
                </span>
                <div className="flex gap-2 ml-4 flex-shrink-0">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      onDelete(payment.id);
                      setShowDeleteConfirm(false);
                    }}
                    className="px-3 py-1.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showVoidConfirm && (
          <motion.tr
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <td
              colSpan={7}
              className="px-6 py-3 bg-orange-50 dark:bg-orange-900/10 border-b border-orange-200 dark:border-orange-800"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-orange-700 dark:text-orange-300 font-medium">
                  Void this accounted payment of{" "}
                  <strong>
                    SR{" "}
                    {parseFloat(payment.amount || 0).toLocaleString("en-SA", {
                      minimumFractionDigits: 2,
                    })}
                  </strong>{" "}
                  from <strong>{payment.clientName}</strong>? This will reverse
                  its accounting entry and restore invoice balances.
                </span>
                <div className="flex gap-2 ml-4 flex-shrink-0">
                  <button
                    onClick={() => setShowVoidConfirm(false)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      onVoid(payment.id, "Voided from payment screen");
                      setShowVoidConfirm(false);
                    }}
                    className="px-3 py-1.5 text-sm rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                  >
                    Void Payment
                  </button>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
};

const ActionIconButton = ({ icon, label, colorClass, onClick }) => (
  <div className="relative group">
    <button
      onClick={onClick}
      className={`p-1.5 rounded-lg transition-colors ${colorClass}`}
      title={label}
    >
      <Icon name={icon} size={15} />
    </button>
    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-50 border border-border">
      {label}
    </div>
  </div>
);

export default PaymentTable;
